import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Store as StoreIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Restaurant as RestaurantIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import api from "../../../services/api";

const DEFAULT_STATS = {
  totalBrands: 0,
  totalStores: 0,
  totalStaff: 0,
  totalProducts: 0,
  recentBrands: [],
  monthlySales: [],
};

const STAT_CARDS_CONFIG = [
  {
    id: "brands",
    title: "ブランド数",
    getValue: (stats) => stats?.totalBrands || 0,
    icon: <StoreIcon sx={{ fontSize: 40 }} />,
    color: "#1976d2",
  },
  {
    id: "stores",
    title: "店舗数",
    getValue: (stats) => stats?.totalStores || 0,
    icon: <StoreIcon sx={{ fontSize: 40 }} />,
    color: "#2e7d32",
  },
  {
    id: "staff",
    title: "スタッフ数",
    getValue: (stats) => stats?.totalStaff || 0,
    icon: <PeopleIcon sx={{ fontSize: 40 }} />,
    color: "#ed6c02",
  },
  {
    id: "products",
    title: "商品数",
    getValue: (stats) => stats?.totalProducts || 0,
    icon: <RestaurantIcon sx={{ fontSize: 40 }} />,
    color: "#9c27b0",
  },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(DEFAULT_STATS);

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        if (process.env.NODE_ENV === 'development') {
          console.log("統計情報の取得を開始");
        }
        const response = await api.get("/api/company/dashboard/stats");
        if (process.env.NODE_ENV === 'development') {
          console.log("APIレスポンス:", response.data);
        }

        if (!response.data) {
          throw new Error("データが取得できませんでした");
        }

        if (isMounted) {
          const newStats = {
            ...DEFAULT_STATS,
            ...response.data,
            recentBrands: Array.isArray(response.data?.recentBrands)
              ? response.data.recentBrands
              : [],
            monthlySales: Array.isArray(response.data?.monthlySales)
              ? response.data.monthlySales
              : [],
          };
          console.log("設定する統計データ:", newStats);
          setStats(newStats);
        }
      } catch (error) {
        console.error("統計情報の取得に失敗しました:", error);
        if (isMounted) {
          setError(
            error.response?.data?.message || "データの取得に失敗しました"
          );
          setStats(DEFAULT_STATS);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <div>
      <Box mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          会社ダッシュボード
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {STAT_CARDS_CONFIG.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.id}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                bgcolor: "background.default",
              }}
            >
              <div style={{ color: card.color }}>{card.icon}</div>
              <Typography variant="h4" component="div" sx={{ mt: 2 }}>
                {card.getValue(stats)}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                {card.title}
              </Typography>
            </Paper>
          </Grid>
        ))}

        {/* ブランド一覧 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="ブランド一覧"
              action={
                <Button
                  size="small"
                  endIcon={<TrendingUpIcon />}
                  onClick={() => navigate("/company-admin/brands")}
                >
                  すべて表示
                </Button>
              }
            />
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 400 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>ブランド名</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>店舗数</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>スタッフ数</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>ステータス</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(stats?.recentBrands) &&
                      stats.recentBrands.map((brand) => (
                        <TableRow key={brand.id || brand._id}>
                          <TableCell>{brand.name}</TableCell>
                          <TableCell>{brand.storeCount}</TableCell>
                          <TableCell>{brand.staffCount}</TableCell>
                          <TableCell>{brand.status}</TableCell>
                        </TableRow>
                      ))}
                    {(!stats?.recentBrands ||
                      stats.recentBrands.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          データがありません
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* 売上統計 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="月間売上統計" />
            <CardContent>
              {Array.isArray(stats?.monthlySales) && stats.monthlySales.length > 0 ? (
                <Box sx={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.monthlySales}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={alpha(theme.palette.text.primary, 0.1)}
                      />
                      <XAxis
                        dataKey="month"
                        stroke={theme.palette.text.secondary}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        tickFormatter={(val) => `¥${(val / 10000).toFixed(0)}万`}
                        stroke={theme.palette.text.secondary}
                        tick={{ fontSize: 12 }}
                      />
                      <RechartsTooltip
                        formatter={(value) => [`¥${value.toLocaleString()}`, "売上"]}
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 8,
                        }}
                      />
                      <Bar
                        dataKey="sales"
                        fill={theme.palette.primary.main}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    売上データがありません
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 最近のアクティビティ */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="最近のアクティビティ" />
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 500 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>日時</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>ブランド</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>店舗</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>アクション</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        データがありません
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default Dashboard;
